import SoundAvatar from './SoundAvatar.jsx';
import { useFontScale } from '../../utils/useFontScale.js';
import '../../styles/theme.css';

/**
 * Header component matching the design artboards.
 *
 * Player header: design/canvas/Main.dc.html top bar
 *   - Left: waveform icon + mono room code
 *   - Right: signature waveform + player name
 *
 * Host header: design/canvas/HostLobby.dc.html top bar
 *   - Clean mono labels, no gradient text
 */
export function Header({
  isHost = false,
  roomCode = '',
  playerCount = 0,
  playerName = '',
  playerAvatar = '🎵',
  soundEnabled = false,
  isConnected = true,
  onEnableSound = null,
  onForceAdvance = null,
  onRestartRoom = null,
  onReturnToLobby = null,
  currentPhase = 'lobby',
}) {
  const { scale, increase, decrease } = useFontScale();

  if (isHost) {
    return (
      <header
        data-testid="host-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 48px',
          borderBottom: '1px solid #1A1F25',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="22" height="22" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M2 9h1.6M5.2 5.4v7.2M8.4 2.6v12.8M11.6 6.2v5.6M14.8 8.2v1.6" stroke="#3ACFCF" strokeWidth="1.7" strokeLinecap="round"/>
            </svg>
            <h1
              style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                fontWeight: 800,
                color: '#EDEFF2',
                letterSpacing: '-0.02em',
                margin: 0,
                padding: 0,
              }}
            >
              SoundDrift
            </h1>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {roomCode && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: '#0D1015',
                border: '1px solid #1C2229',
                borderRadius: '12px',
                padding: '8px 20px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  color: '#6E7982',
                  fontWeight: 600,
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                }}
              >
                Room
              </span>
              <span
                data-testid="header-room-code"
                style={{
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  color: '#EDEFF2',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                {roomCode.toUpperCase()}
              </span>
            </div>
          )}

          <span
            style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '12px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#79838D',
            }}
          >
            {playerCount} {playerCount === 1 ? 'Player' : 'Players'}
          </span>

          {soundEnabled ? (
            <div
              data-testid="audio-status-badge"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#8FC7A0',
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: '#8FC7A0',
                  display: 'inline-block',
                }}
              />
              Sound Active
            </div>
          ) : (
            /* Status chip, not a second gate: the host lobby renders the
               actual enable-sound CTA. Two controls with the same label put
               two identical buttons on the host screen. */
            <button
              type="button"
              data-testid="audio-status-badge"
              onClick={onEnableSound}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#E0A458',
                background: 'none',
                border: 'none',
                cursor: onEnableSound ? 'pointer' : 'default',
                padding: 0,
                minHeight: '44px',
                minWidth: '44px',
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: '#E0A458',
                  display: 'inline-block',
                }}
              />
              Sound muted
            </button>
          )}

          {isHost && currentPhase !== 'lobby' && onReturnToLobby && (
            <button
              type="button"
              data-testid="header-return-lobby-btn"
              onClick={onReturnToLobby}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                padding: '0 16px',
                borderRadius: '10px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line, #1A1F25)',
                color: 'var(--sd-teal, #3ACFCF)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--sd-teal, #3ACFCF)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--sd-line, #1A1F25)'; }}
            >
              🏠 Return to Lobby
            </button>
          )}

          {isHost && onRestartRoom && (
            <button
              type="button"
              data-testid="header-restart-room-btn"
              onClick={onRestartRoom}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                padding: '0 16px',
                borderRadius: '10px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line, #1A1F25)',
                color: 'var(--sd-dim, #6E7982)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--sd-ink, #EDEFF2)';
                e.currentTarget.style.color = 'var(--sd-ink, #EDEFF2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--sd-line, #1A1F25)';
                e.currentTarget.style.color = 'var(--sd-dim, #6E7982)';
              }}
            >
              🔄 New Room
            </button>
          )}

          {isHost && currentPhase !== 'lobby' && onForceAdvance && (
            <button
              type="button"
              data-testid="header-force-advance-btn"
              onClick={onForceAdvance}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                padding: '0 18px',
                borderRadius: '10px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line, #1A1F25)',
                color: 'var(--sd-amber, #E0A458)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--sd-amber, #E0A458)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--sd-line, #1A1F25)'; }}
            >
              ⏩ Force Advance
            </button>
          )}

          {/* Text Size Scale Stepper */}
          <div
            data-testid="font-scale-adjuster"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--sd-panel-2, #0E1116)',
              border: '1px solid var(--sd-line, #1A1F25)',
              borderRadius: '10px',
              padding: '2px 4px',
              gap: '2px',
              height: '44px',
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              data-testid="font-decrease-btn"
              onClick={decrease}
              title="Decrease text size"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--sd-ink-2, #C6CDD4)',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                minWidth: '28px',
              }}
            >
              A-
            </button>
            <span
              style={{
                fontSize: '11px',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                color: 'var(--sd-dim, #6E7982)',
                minWidth: '36px',
                textAlign: 'center',
                userSelect: 'none',
              }}
            >
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              data-testid="font-increase-btn"
              onClick={increase}
              title="Increase text size (TV 10-foot view)"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--sd-ink-2, #C6CDD4)',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                minWidth: '28px',
              }}
            >
              A+
            </button>
          </div>
        </div>
      </header>
    );
  }

  // Player Header — matches design/canvas/Main.dc.html + Waiting.dc.html top bar
  return (
    <header
      data-testid="player-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px 12px',
        borderBottom: '1px solid #1A1F25',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <h1 style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>SoundDrift</h1>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M2 9h1.6M5.2 5.4v7.2M8.4 2.6v12.8M11.6 6.2v5.6M14.8 8.2v1.6" stroke="#3ACFCF" strokeWidth="1.7" strokeLinecap="round"/>
        </svg>
        {roomCode && (
          <span
            data-testid="header-room-code"
            style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '0.16em',
              color: '#EDEFF2',
            }}
          >
            {roomCode.toUpperCase()}
          </span>
        )}
      </div>

      {playerName && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <SoundAvatar
            size="sig"
            seed={typeof playerAvatar === 'string' ? playerAvatar : playerName}
            clip={typeof playerAvatar === 'object' ? playerAvatar : null}
            style={{
              width: '34px',
              height: '15px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              flex: 'none',
            }}
          />
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#C6CDD4' }}>
            {playerName}
          </span>
          <span
            title={isConnected ? 'Connected' : 'Disconnected'}
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: isConnected ? '#8FC7A0' : '#E4738A',
              display: 'inline-block',
            }}
          />
        </div>
      )}
    </header>
  );
}

export default Header;
